import { createContext, useContext, useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { guardrailsApi, type PublishToCiResult } from '@/services/guardrailsApi';

const STORAGE_KEY = 'publish-to-ci-job';
const POLL_INTERVAL = 3000;
const MAX_POLL_TIME = 120_000;

interface StoredJob {
  jobId: string;
  startedAt: number;
}

interface PublishJobState {
  publishing: boolean;
  result: PublishToCiResult | null;
  error: string | null;
  start: () => Promise<void>;
  dismiss: () => void;
}

const PublishJobContext = createContext<PublishJobState | null>(null);

function readStoredJob(): StoredJob | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored: StoredJob = JSON.parse(raw);
    if (Date.now() - stored.startedAt > MAX_POLL_TIME) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return stored;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function PublishJobProvider({ children }: { children: ReactNode }) {
  const [publishing, setPublishing] = useState(() => readStoredJob() !== null);
  const [result, setResult] = useState<PublishToCiResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const finish = useCallback((res: PublishToCiResult | null, err: string | null) => {
    clearTimer();
    localStorage.removeItem(STORAGE_KEY);
    if (!mountedRef.current) return;
    setPublishing(false);
    setResult(res);
    setError(err);
  }, [clearTimer]);

  const poll = useCallback((jobId: string, startedAt: number) => {
    if (Date.now() - startedAt > MAX_POLL_TIME) {
      finish(null, 'PR creation timed out');
      return;
    }

    timerRef.current = setTimeout(async () => {
      if (!mountedRef.current) return;
      try {
        const job = await guardrailsApi.getJob(jobId);
        if (!mountedRef.current) return;
        if (job.status === 'completed' && job.result) {
          finish(job.result, null);
        } else if (job.status === 'failed') {
          finish(null, job.error || 'PR creation failed');
        } else {
          poll(jobId, startedAt);
        }
      } catch {
        if (mountedRef.current) poll(jobId, startedAt);
      }
    }, POLL_INTERVAL);
  }, [finish]);

  // On mount, resume polling if there's a pending job
  useEffect(() => {
    mountedRef.current = true;
    const stored = readStoredJob();
    if (stored) {
      setPublishing(true);
      poll(stored.jobId, stored.startedAt);
    }
    return () => {
      mountedRef.current = false;
      clearTimer();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const start = useCallback(async () => {
    setPublishing(true);
    setResult(null);
    setError(null);
    try {
      const jobId = await guardrailsApi.startPublishToCi();
      const startedAt = Date.now();
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ jobId, startedAt }));
      poll(jobId, startedAt);
    } catch (err: unknown) {
      const axiosMsg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      const message = axiosMsg || (err instanceof Error ? err.message : 'Failed to start publish');
      finish(null, message);
    }
  }, [poll, finish]);

  const dismiss = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return (
    <PublishJobContext.Provider value={{ publishing, result, error, start, dismiss }}>
      {children}
    </PublishJobContext.Provider>
  );
}

export function usePublishJob(): PublishJobState {
  const ctx = useContext(PublishJobContext);
  if (!ctx) throw new Error('usePublishJob must be used within PublishJobProvider');
  return ctx;
}
